/**
 * Simulation frame generator for the 滑雪智能鞋垫逐弯复盘网站.
 *
 * Spec: PO-5.1
 *
 * This is the core simulation engine. It synthesizes 3500 frames of
 * `SkiTickData` plus 3500 frames of `DualPressureArray` (86-point pressure
 * arrays for both feet) at 100 Hz, following the 10-turn run script defined
 * in `./turn-script.ts`.
 *
 * Determinism (PO-5.4): every random draw is produced by an LCG-seeded
 * Box-Muller transform, so the same input script always yields byte-identical
 * output across runs.
 */

import {
  SkiTickData,
  DualPressureArray,
  PressureSensorArray,
  PressurePoint,
  Vector3,
  Quaternion,
} from '../types/ski-data';
import { TurnDirection } from '../types/enums';
import {
  PRESSURE_POINT_COORDINATES,
  PRESSURE_REGION_RANGES,
  PRESSURE_POINT_COUNT,
} from './pressure-coordinates';
import { RunScript, TurnScriptEntry } from './turn-script';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Standard gravity, m/s². */
const GRAVITY = 9.81;

/** Base forward speed, m/s. */
const BASE_SPEED = 8.0;

/** Base heart rate, bpm. */
const BASE_HEART_RATE = 120;

/** Constant magnetic field, µT. */
const MAGNETOMETER_BASE: Readonly<Vector3> = { x: 25, y: -5, z: 40 };

/** Turn-phase boundaries as a fraction of turn duration. */
const ENTRY_END = 0.3; // first 30% — entry
const CONTROL_END = 0.7; // next 40% — control (last 30% — exit)

// Noise standard deviations (per spec noise model).
const NOISE_GYRO = 0.02; // rad/s
const NOISE_ACCEL = 0.1; // m/s²
const NOISE_EDGE = 0.5; // degrees
const NOISE_PRESSURE = 0.02; // normalized 0-1
const NOISE_SPEED = 0.5; // m/s  (≈ ±1.0 fluctuation)
const NOISE_HR = 2.5; // bpm  (≈ ±5 fluctuation)
const NOISE_MAG = 0.25; // µT   (≈ ±0.5)

/** Relative spatial noise applied per pressure point (±3%). */
const PRESSURE_SPATIAL_NOISE = 0.03;

/** Scale factor turning gyro_z into a quaternion bank angle. */
const QUATERNION_BANK_SCALE = 0.3;

// ---------------------------------------------------------------------------
// Deterministic RNG / math helpers
// ---------------------------------------------------------------------------

/** Linear interpolation between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Hermite smoothstep: returns 0 at `edge0`, 1 at `edge1`, C1-continuous. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Clamp `value` to the closed range [`lo`, `hi`]. */
function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}

/**
 * Deterministic Gaussian noise via the Box-Muller transform.
 *
 * A linear congruential generator (LCG, glibc-style parameters) seeded by
 * `seed` produces the two uniform variates that Box-Muller consumes. Because
 * the LCG is a pure function of `seed`, the same seed always yields the same
 * sample — this is what makes the whole simulation reproducible (PO-5.4).
 */
export function gaussianNoise(seed: number, mean: number, std: number): number {
  // state_{n+1} = (a * state_n + c) mod 2^31, masked to positive 31-bit.
  const s1 = (Math.imul(seed | 0, 1103515245) + 12345) & 0x7fffffff;
  const u1 = s1 / 0x7fffffff;
  const s2 = (Math.imul(s1, 1103515245) + 12345) & 0x7fffffff;
  const u2 = s2 / 0x7fffffff;
  // Guard against log(0).
  const safeU1 = u1 < 1e-10 ? 1e-10 : u1;
  const z = Math.sqrt(-2 * Math.log(safeU1)) * Math.cos(2 * Math.PI * u2);
  return mean + std * z;
}

/**
 * 32-bit FNV-1a hash. Used to mix (point index, timestamp, phase, foot) into
 * a well-distributed seed so each pressure point draws an independent noise
 * sample while remaining fully deterministic.
 */
function fnv1a(...parts: number[]): number {
  let h = 2166136261 >>> 0;
  for (const p of parts) {
    h ^= p >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Turn phase logic
// ---------------------------------------------------------------------------

type TurnPhase = 'entry' | 'control' | 'exit';

interface ActiveTurn {
  turn: TurnScriptEntry;
  progress: number; // 0..1 within [start_ms, end_ms)
  intensity: number; // 0..1 ramp multiplier (1 during control)
  phase: TurnPhase;
}

/** Compute the 0..1 turn intensity (ramp multiplier) for a given progress. */
function intensityFromProgress(progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return 0;
  if (progress < ENTRY_END) return smoothstep(0, ENTRY_END, progress);
  if (progress < CONTROL_END) return 1;
  return 1 - smoothstep(CONTROL_END, 1, progress);
}

/** Compute the turn intensity at an arbitrary timestamp (0 outside the turn). */
function intensityAtTime(turn: TurnScriptEntry, t: number): number {
  if (t <= turn.start_ms || t >= turn.end_ms) return 0;
  const progress = (t - turn.start_ms) / turn.duration_ms;
  return intensityFromProgress(progress);
}

/** Locate the turn whose [start_ms, end_ms) window contains `t`, if any. */
function findActiveTurn(script: RunScript, t: number): ActiveTurn | null {
  for (const turn of script.turns) {
    if (t >= turn.start_ms && t < turn.end_ms) {
      const progress = (t - turn.start_ms) / turn.duration_ms;
      let phase: TurnPhase;
      if (progress < ENTRY_END) phase = 'entry';
      else if (progress < CONTROL_END) phase = 'control';
      else phase = 'exit';
      return { turn, progress, intensity: intensityFromProgress(progress), phase };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// 86-point pressure array synthesis
// ---------------------------------------------------------------------------

/**
 * Build the 86-point `PressureSensorArray` for a single foot.
 *
 * @param baseScalar  Normalized scalar pressure for this foot (0..1).
 * @param comForeAft  Center-of-mass fore/aft bias (-1..1); shifts load between
 *                    the forefoot (+) and heel (-).
 * @param timestampMs Frame timestamp; used for deterministic noise seeding.
 * @param turnPhase   Turn progress (0..1) within the active turn, or 0 for
 *                    straight frames.
 * @param footId      0 = left, 1 = right (right foot mirrors Y).
 */
function buildFootArray(
  baseScalar: number,
  comForeAft: number,
  timestampMs: number,
  turnPhase: number,
  footId: number,
): PressureSensorArray {
  const points: PressurePoint[] = [];
  const forefoot: PressurePoint[] = [];
  const arch: PressurePoint[] = [];
  const heel: PressurePoint[] = [];

  const ts = Math.round(timestampMs);
  const phaseCode = Math.round(turnPhase * 100000);

  for (const coord of PRESSURE_POINT_COORDINATES) {
    const idx = coord.index;

    // Region-specific base value (fore/aft COM shift).
    let value: number;
    if (idx <= PRESSURE_REGION_RANGES.forefoot.end) {
      value = baseScalar + comForeAft * 0.05;
    } else if (idx <= PRESSURE_REGION_RANGES.arch.end) {
      value = baseScalar;
    } else {
      value = baseScalar - comForeAft * 0.05;
    }

    // Deterministic ±3% spatial noise (seed = point index + frame timestamp).
    const seed = fnv1a(idx, ts, footId, phaseCode);
    value *= 1 + gaussianNoise(seed, 0, PRESSURE_SPATIAL_NOISE);
    value = clamp(value, 0, 1);

    // Right foot mirrors the Y axis (per pressure-coordinates spec).
    const y = footId === 0 ? coord.y : -coord.y;
    const point: PressurePoint = { index: idx, x: coord.x, y, value };

    points.push(point);
    if (idx <= PRESSURE_REGION_RANGES.forefoot.end) forefoot.push(point);
    else if (idx <= PRESSURE_REGION_RANGES.arch.end) arch.push(point);
    else heel.push(point);
  }

  return { points, forefoot, arch, heel };
}

/**
 * Synthesize a full dual-foot 86-point pressure array for one frame.
 *
 * Each foot starts from the 86 base coordinates in `PRESSURE_POINT_COORDINATES`:
 *   - Forefoot (0-39):  baseScalar + comForeAft * 0.05
 *   - Arch    (40-65):  baseScalar
 *   - Heel    (66-85):  baseScalar - comForeAft * 0.05
 * A deterministic ±3% spatial noise is added per point (seeded from the point
 * index and the frame timestamp), then every value is clamped to [0, 1].
 *
 * The equal-weight mean of the 86 points matches the input `pressureLeft` /
 * `pressureRight` within 5%: the COM shift contributes at most ~1.2% bias
 * (uneven forefoot/heel counts: 40 vs 20) and the zero-mean noise averages
 * out over 86 samples (<0.4% at 2σ).
 *
 * @param pressureLeft  Normalized scalar pressure for the left foot.
 * @param pressureRight Normalized scalar pressure for the right foot.
 * @param comForeAft    COM fore/aft bias (-1..1).
 * @param turnPhase     Turn progress (0..1) within the active turn, or 0 for
 *                      straight frames.
 * @param timestampMs   Frame timestamp; stored on the result and used to seed
 *                      the per-point spatial noise deterministically.
 */
export function synthesizePressureArray(
  pressureLeft: number,
  pressureRight: number,
  comForeAft: number,
  turnPhase: number,
  timestampMs: number,
): DualPressureArray {
  const left = buildFootArray(pressureLeft, comForeAft, timestampMs, turnPhase, 0);
  const right = buildFootArray(pressureRight, comForeAft, timestampMs, turnPhase, 1);
  return { left, right, timestamp_ms: timestampMs };
}

// ---------------------------------------------------------------------------
// Main simulation entry point
// ---------------------------------------------------------------------------

/**
 * Generate the full synthetic run: `frameCount` frames of `SkiTickData` plus
 * an equal number of `DualPressureArray` frames, sampled at `sample_rate` Hz,
 * shaped by the 10-turn `script`.
 */
export function generateSimulationData(script: RunScript): {
  frames: SkiTickData[];
  pressureArrays: DualPressureArray[];
} {
  // Sanity check: the coordinate table must define exactly 86 points.
  if (PRESSURE_POINT_COORDINATES.length !== PRESSURE_POINT_COUNT) {
    throw new Error(
      `Expected ${PRESSURE_POINT_COUNT} pressure coordinates, got ${
        PRESSURE_POINT_COORDINATES.length
      }`,
    );
  }

  const totalFrames = script.frame_count;
  const intervalMs = 1000 / script.sample_rate; // 10 ms at 100 Hz

  const frames: SkiTickData[] = new Array<SkiTickData>(totalFrames);
  const pressureArrays: DualPressureArray[] = new Array<DualPressureArray>(totalFrames);

  for (let i = 0; i < totalFrames; i++) {
    const t = i * intervalMs;
    const active = findActiveTurn(script, t);

    // Unique deterministic seed base for this frame. 7919 is prime and larger
    // than the per-field offset range (1..16), so every (frame, field) pair
    // gets a distinct, collision-free seed.
    const seedBase = i * 7919;

    // ----- Speed (computed first; centripetal accel depends on it) -----
    const speed = clamp(
      BASE_SPEED * (active ? 0.95 : 1.0) +
        gaussianNoise(seedBase + 11, 0, NOISE_SPEED),
      0,
      30,
    );

    // ----- Gyroscope Z (signed turn rate) -----
    let gyroZ: number;
    if (active) {
      const peak = active.turn.peak_gyro_z;
      if (active.phase === 'control') {
        // Maintain peak with ±5% random fluctuation.
        gyroZ = peak + gaussianNoise(seedBase + 14, 0, Math.abs(peak) * 0.025);
      } else {
        gyroZ = peak * active.intensity;
      }
      gyroZ += gaussianNoise(seedBase + 1, 0, NOISE_GYRO);
    } else {
      // Straight: 0 ± sensor noise.
      gyroZ = gaussianNoise(seedBase + 1, 0, NOISE_GYRO);
    }

    // ----- Gyroscope X / Y (small, derived from edge-angle dynamics) -----
    const edgeDynamic = active
      ? active.intensity * Math.sign(active.turn.peak_gyro_z) * 0.03
      : 0;
    const gyroX = edgeDynamic + gaussianNoise(seedBase + 2, 0, NOISE_GYRO);
    const gyroY = gaussianNoise(seedBase + 3, 0, NOISE_GYRO);

    // ----- Acceleration -----
    // ax: turn centripetal force (gyro_z * speed) ± 0.1 noise.
    // ay: small lateral ± 0.1 noise.
    // az: gravity (9.81) ± 0.1 noise.
    const accelX = gyroZ * speed + gaussianNoise(seedBase + 4, 0, NOISE_ACCEL);
    const accelY = gaussianNoise(seedBase + 5, 0, NOISE_ACCEL);
    const accelZ = GRAVITY + gaussianNoise(seedBase + 6, 0, NOISE_ACCEL);
    const acceleration: Vector3 = { x: accelX, y: accelY, z: accelZ };

    // ----- Pressure (left/right scalars) -----
    // Straight: both ~0.5 ± 0.02.
    // Left turn (gyro_z > 0): right foot is outside → pressure_right ramps to
    //   outside_pressure_peak, pressure_left = 1 - pressure_right.
    // Right turn (gyro_z < 0): left foot is outside → mirror of the above.
    let pressureLeft: number;
    let pressureRight: number;
    if (active) {
      const outsidePressure = lerp(
        0.5,
        active.turn.outside_pressure_peak,
        active.intensity,
      );
      const insidePressure = 1 - outsidePressure;
      if (active.turn.direction === TurnDirection.Left) {
        pressureRight = outsidePressure + gaussianNoise(seedBase + 8, 0, NOISE_PRESSURE);
        pressureLeft = insidePressure + gaussianNoise(seedBase + 7, 0, NOISE_PRESSURE);
      } else {
        pressureLeft = outsidePressure + gaussianNoise(seedBase + 7, 0, NOISE_PRESSURE);
        pressureRight = insidePressure + gaussianNoise(seedBase + 8, 0, NOISE_PRESSURE);
      }
    } else {
      pressureLeft = 0.5 + gaussianNoise(seedBase + 7, 0, NOISE_PRESSURE);
      pressureRight = 0.5 + gaussianNoise(seedBase + 8, 0, NOISE_PRESSURE);
    }
    pressureLeft = clamp(pressureLeft, 0, 1);
    pressureRight = clamp(pressureRight, 0, 1);

    // ----- Edge angles (right board delayed by edging_sync_offset ms) -----
    // Both boards ramp to avg_edge_angle; the right board's timeline is shifted
    // back by edging_sync_offset so it lags — large offsets model async edging.
    let edgeAngleLeft: number;
    let edgeAngleRight: number;
    if (active) {
      const turn = active.turn;
      const intensityLeft = active.intensity;
      const intensityRight = intensityAtTime(turn, t - turn.edging_sync_offset);
      edgeAngleLeft =
        turn.avg_edge_angle * intensityLeft + gaussianNoise(seedBase + 9, 0, NOISE_EDGE);
      edgeAngleRight =
        turn.avg_edge_angle * intensityRight + gaussianNoise(seedBase + 10, 0, NOISE_EDGE);
    } else {
      edgeAngleLeft = gaussianNoise(seedBase + 9, 0, NOISE_EDGE);
      edgeAngleRight = gaussianNoise(seedBase + 10, 0, NOISE_EDGE);
    }
    edgeAngleLeft = Math.max(0, edgeAngleLeft);
    edgeAngleRight = Math.max(0, edgeAngleRight);

    // ----- Heart rate (120 ± 5 bpm) -----
    const heartRate = Math.round(
      BASE_HEART_RATE + gaussianNoise(seedBase + 12, 0, NOISE_HR),
    );

    // ----- Quaternion (bank angle derived from gyro_z) -----
    // angle = gyro_z * 0.3; w = cos(half), z = sin(half), x = y = 0.
    const bankAngle = gyroZ * QUATERNION_BANK_SCALE;
    const halfAngle = bankAngle / 2;
    const quaternion: Quaternion = {
      w: Math.cos(halfAngle),
      x: 0,
      y: 0,
      z: Math.sin(halfAngle),
    };

    // ----- Magnetometer (constant field [25, -5, 40] ± 0.5) -----
    const magnetometer: Vector3 = {
      x: MAGNETOMETER_BASE.x + gaussianNoise(seedBase + 13, 0, NOISE_MAG),
      y: MAGNETOMETER_BASE.y + gaussianNoise(seedBase + 15, 0, NOISE_MAG),
      z: MAGNETOMETER_BASE.z + gaussianNoise(seedBase + 16, 0, NOISE_MAG),
    };

    // ----- Assemble the tick -----
    frames[i] = {
      timestamp_ms: t,
      acceleration,
      gyroscope: { x: gyroX, y: gyroY, z: gyroZ },
      pressure_left: pressureLeft,
      pressure_right: pressureRight,
      edge_angle_left: edgeAngleLeft,
      edge_angle_right: edgeAngleRight,
      speed,
      heart_rate: heartRate,
      timestamp: Math.round(t),
      accelerometer: { x: accelX, y: accelY, z: accelZ },
      magnetometer,
      quaternion,
    };

    // ----- 86-point pressure array -----
    // COM shifts slightly forward during a turn (fore-aft bias grows with
    // turn intensity). turnPhase is the in-turn progress (0 for straight).
    const comForeAft = active ? active.intensity * 0.6 : 0;
    const turnPhase = active ? active.progress : 0;
    pressureArrays[i] = synthesizePressureArray(
      pressureLeft,
      pressureRight,
      comForeAft,
      turnPhase,
      t,
    );
  }

  return { frames, pressureArrays };
}

// ---------------------------------------------------------------------------
// Verification (PO-5.1)
// ---------------------------------------------------------------------------
//
//   Total frames              : 3500  (SkiTickData + DualPressureArray each)
//   First frame timestamp_ms  : 0
//   Last frame  timestamp_ms  : 34990   ((frameCount - 1) * 10 ms)
//   Adjacent frame interval   : 10 ms   (100 Hz, within 8-12 ms tolerance)
//   Pressure-array validation : the equal-weight mean of each 86-point foot
//                               array matches the input pressureLeft /
//                               pressureRight scalar within 5% (COM bias
//                               ≤ ~1.2% from uneven forefoot/heel counts;
//                               zero-mean spatial noise averages to <0.4%
//                               at 2σ over 86 samples).
//   Determinism               : identical input scripts yield identical output
//                               (LCG-seeded Box-Muller, PO-5.4).
// ---------------------------------------------------------------------------
